import { getAuth, onAuthStateChanged, User } from 'firebase/auth'
import { useCallback, useEffect, useState } from 'react'

const readFlag = (storageKey: string) => {
  try {
    return window.localStorage.getItem(storageKey) === 'true'
  } catch (error) {
    console.error(`Failed to read ${storageKey}`, error)
    return false
  }
}

const writeFlag = (storageKey: string, value: boolean) => {
  try {
    if (value) {
      window.localStorage.setItem(storageKey, 'true')
    } else {
      window.localStorage.removeItem(storageKey)
    }
  } catch (error) {
    console.error(`Failed to update ${storageKey}`, error)
  }
}

// Survives a page reload, stays in sync between tabs and is cleared on sign-out,
// or on sign-in of a user matching `clearFor` (it should be a stable reference)
export const useSignOutScopedFlag = (
  storageKey: string,
  clearFor?: (user: User) => boolean
) => {
  const [isSet, setIsSet] = useState(() => readFlag(storageKey))

  const set = useCallback(() => {
    setIsSet(true)
    writeFlag(storageKey, true)
  }, [storageKey])

  const clear = useCallback(() => {
    setIsSet(false)
    writeFlag(storageKey, false)
  }, [storageKey])

  useEffect(() => {
    return onAuthStateChanged(getAuth(), user => {
      if (!user || clearFor?.(user)) {
        clear()
      }
    })
  }, [clear, clearFor])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setIsSet(event.newValue === 'true')
      }

      if (event.key === null) {
        setIsSet(readFlag(storageKey))
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey])

  return { isSet, set, clear }
}
